import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Box, Typography } from '@mui/material';
import { Image } from '@provider-portal/promotions-common/types';

import { getCurrencySymbol } from '../../utils/currency';
import { CreatePromotionButton } from '../CreatePromotionButton/CreatePromotionButton';

export interface PromotionTileProps {
  strings?: {
    weeklySpend: string;
    weeklyImpact: string;
    addPromotion: string;
    weeklyImpactValue: string;
  };
  title: string;
  description?: string;
  weeklySpendValue?: string;
  weeklyImpactValue?: string;
  weeklyImpactTemplate?: string;
  onClickNewPromotion: () => void;
  backgroundImage?: Image;
  currency: string;
  backgroundColourOverride?: string;
}

const styles = {
  focus: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '24px',
    gap: '16px',
    backgroundColor: '#F4F5F6',
    borderRadius: '16px',
    flex: 'none',
    order: 0,
    flexGrow: 1,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  detailsWithBackground: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  description: {
    color: 'colors.neutral700',
  },
} as const;
const DEFAULT_CURRENCY = 'EUR';
export const PromotionTile = memo(
  ({
    weeklySpendValue,
    title,
    description,
    onClickNewPromotion,
    weeklyImpactValue,
    backgroundImage,
    currency,
    backgroundColourOverride,
  }: PromotionTileProps) => {
    const intl = useIntl();
    const currencyTrimmed = currency.trim() || DEFAULT_CURRENCY;

    const useStrings = useMemo(
      () => ({
        weeklySpend: intl.formatMessage({ id: 'components.PromotionFocus.weeklySpend' }),
        weeklyImpact: intl.formatMessage({ id: 'components.PromotionFocus.weeklyImpact' }),
        addPromotion: intl.formatMessage({ id: 'components.PromotionFocus.addPromotion' }),
        weeklySpendValue: intl.formatMessage(
          { id: 'components.PromotionFocus.weeklySpendValueTemplate' },
          { value: weeklySpendValue, currency: getCurrencySymbol(intl, currencyTrimmed) },
        ),
        weeklyImpactValue: intl.formatMessage(
          { id: 'components.PromotionFocus.weeklyImpactValueTemplate' },
          { value: weeklyImpactValue, currency: getCurrencySymbol(intl, currencyTrimmed) },
        ),
      }),
      [currencyTrimmed, intl, weeklyImpactValue, weeklySpendValue],
    );

    const containerStyle = useMemo(() => {
      let style: Record<string, string | number> = styles.focus;

      if (backgroundImage) {
        style = {
          ...style,
          backgroundImage: `url(${backgroundImage.url})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom right',
          backgroundSize: backgroundImage.width,
          backgroundColor: backgroundColourOverride || style.backgroundColor,
        };
      }

      return style;
    }, [backgroundImage, backgroundColourOverride]);

    return (
      <Box sx={containerStyle}>
        <Typography variant="h6">{title}</Typography>
        <Box sx={backgroundImage ? styles.detailsWithBackground : styles.details}>
          {description && (
            <Typography mb={1} variant="bodyS" sx={styles.description}>
              {description}
            </Typography>
          )}
          <Typography variant="bodyS">
            {useStrings.weeklySpend}:{' '}
            <Typography variant="subtitleS">{useStrings.weeklySpendValue}</Typography>
          </Typography>
          <Typography variant="bodyS">
            {useStrings.weeklyImpact}:{' '}
            <Typography variant="subtitleS">{useStrings.weeklyImpactValue}</Typography>
          </Typography>
        </Box>
        <CreatePromotionButton onClick={onClickNewPromotion} />
      </Box>
    );
  },
);
